import { apiClient } from '@/shared/services/apiClient';

export interface UserSettings {
  user_id: string;
  organization_name: string | null;
  system_email: string | null;
  call_message: string | null;
  call_auto_record: boolean;
  call_hide_self_view: boolean;
  call_use_back_camera: boolean;
  scheduled_call_message: string | null;
  scheduled_reminder_message: string | null;
  request_recording_message: string | null;
  request_auto_reminder: boolean;
  notify_on_link_share: boolean;
  notify_auto_recording: boolean;
  email_notifications: boolean;
  push_notifications: boolean;
  sms_notifications: boolean;
  weekly_reports: boolean;
  product_updates: boolean;
  security_alerts: boolean;
  marketing_emails: boolean;
  extras: Record<string, any>;
  created_at: string;
  updated_at: string;
}

/**
 * Load user settings from backend API
 * Uses backend which handles authentication and creates defaults if needed
 */
export async function loadOrCreateUserSettings(): Promise<UserSettings> {
  try {
    console.log('[userSettings] Loading settings from backend API');
    const response = await apiClient.get<{ ok: boolean; settings: UserSettings }>('/user/settings');
    return response.settings;
  } catch (error) {
    console.error('[userSettings] Error loading settings from backend:', error);
    throw error;
  }
}

/**
 * Save partial user settings via backend API
 * Backend handles upsert and syncing to customers table
 */
export async function saveUserSettings(patch: Partial<UserSettings>): Promise<UserSettings> {
  try {
    console.log('[userSettings] Saving settings to backend API:', patch);
    const response = await apiClient.post<{ ok: boolean; settings: UserSettings; message: string }>('/user/settings', patch);
    return response.settings;
  } catch (error) {
    console.error('[userSettings] Error saving settings to backend:', error);
    throw error;
  }
}

/**
 * Get organization name from backend API (fallback)
 * Backend queries customers table if not in user_settings
 */
export async function getOrganizationNameFromCustomers(userId: string): Promise<string | null> {
  try {
    // Backend already handles this fallback in getUserSettings
    // This function is kept for compatibility but delegates to backend
    const settings = await loadOrCreateUserSettings();
    return settings.organization_name || null;
  } catch (error) {
    console.error('[userSettings] Error fetching organization name:', error);
    return null;
  }
}
